// Auth state management for roles, permissions, and menu access

// Effective HQ-transaction list filter for a user: SuperAdmins are never filtered;
// everyone else is filtered to HQ-only when they can process HQ transactions. Mirrors
// the backend's getAllTransaction decision so the UI can react to genuine changes.
const isHqFilterActive = (roles, canProcessHqTx) => {
  const isSuperAdmin = (roles || []).some((r) => (r || "").toLowerCase() === "superadmin");
  return !isSuperAdmin && canProcessHqTx === true;
};

// initial state
const state = () => ({
  user: null,
  roles: [],
  permissions: [],
  allowedMenuItems: [],
  // Live HQ-transaction processing capability, kept fresh by the periodic
  // /permission/list poll (see main.js). Drives the transaction list's HQ-filter
  // refresh without a re-login.
  canProcessHqTx: false,
  // True once the first authoritative /permission/list load of this session has been
  // applied. The login response no longer carries canProcessHqTx, so the very first
  // refresh is the app *discovering* the user's real capability — NOT a mid-session
  // change — and must not trigger a list reload/toast. Resets on every page load, so a
  // refresh always re-establishes the baseline silently.
  hqBaselineSet: false,
  token: null,
});

// getters
const getters = {
  // Check if user has a specific role
  hasRole: (state) => (role) => state.roles.includes(role),

  // Check if user has any of the provided roles
  hasAnyRole: (state) => (roles) => roles.some((role) => state.roles.includes(role)),

  // Check if user has all of the provided roles
  hasAllRoles: (state) => (roles) => roles.every((role) => state.roles.includes(role)),

  // Check if user has a specific permission
  hasPermission: (state) => (permission) => state.permissions.includes(permission),

  // Check if user has any of the provided permissions
  hasAnyPermission: (state) => (permissions) => permissions.some((permission) => state.permissions.includes(permission)),

  // Check if user has all of the provided permissions
  hasAllPermissions: (state) => (permissions) => permissions.every((permission) => state.permissions.includes(permission)),

  // Check if menu item is allowed
  isMenuAllowed: (state) => (menuItem) => state.allowedMenuItems.includes(menuItem),

  // Get all permissions
  allPermissions: (state) => state.permissions,

  // Get all roles
  allRoles: (state) => state.roles,

  // Get all allowed menu items
  allAllowedMenuItems: (state) => state.allowedMenuItems,

  // Whether the user can process HQ transactions (live-polled)
  canProcessHqTx: (state) => state.canProcessHqTx,

  // Get current user
  currentUser: (state) => state.user,

  // Check if user is authenticated
  isAuthenticated: (state) => !!state.token,
};

// actions
const actions = {
  // Set auth data from login response
  setAuthData({ commit }, authData) {
    commit("SET_ROLES", authData.roles || []);
    commit("SET_PERMISSIONS", authData.permissions || []);
    commit("SET_ALLOWED_MENU_ITEMS", authData.allowedMenuItems || []);
    // The login response no longer carries canProcessHqTx; default to false and let
    // the first refreshPermissions() (on app load / focus) hydrate the live value.
    commit("SET_CAN_PROCESS_HQ_TX", !!authData.canProcessHqTx);
    commit("SET_TOKEN", authData.token);

    // Also save to localStorage for persistence
    localStorage.setItem("userRoles", JSON.stringify(authData.roles || []));
    localStorage.setItem("userPermissions", JSON.stringify(authData.permissions || []));
    localStorage.setItem("allowedMenuItems", JSON.stringify(authData.allowedMenuItems || []));
    localStorage.setItem("canProcessHqTx", JSON.stringify(!!authData.canProcessHqTx));
    // localStorage.setItem("currentUser", JSON.stringify(authData.user));
  },

  // Load auth data from localStorage (for page refresh)
  async loadAuthData({ commit, dispatch }) {
    try {
      const token = localStorage.getItem("jwtToken");
      const user = localStorage.getItem("currentUser");
      const roles = localStorage.getItem("userRoles");
      const permissions = localStorage.getItem("userPermissions");
      const allowedMenuItems = localStorage.getItem("allowedMenuItems");
      const canProcessHqTx = localStorage.getItem("canProcessHqTx");

      // Load basic data immediately from localStorage (fast)
      if (token) commit("SET_TOKEN", token);
      if (user) commit("SET_USER", JSON.parse(user));
      if (roles) commit("SET_ROLES", JSON.parse(roles));
      if (permissions) commit("SET_PERMISSIONS", JSON.parse(permissions));
      if (allowedMenuItems) commit("SET_ALLOWED_MENU_ITEMS", JSON.parse(allowedMenuItems));
      if (canProcessHqTx) commit("SET_CAN_PROCESS_HQ_TX", JSON.parse(canProcessHqTx));

      console.log("Loaded cached permissions from localStorage:", {
        roles: JSON.parse(roles || '[]'),
        permissions: JSON.parse(permissions || '[]'),
        allowedMenuItems: JSON.parse(allowedMenuItems || '[]')
      });

      // Then fetch fresh permissions from server (to get latest updates)
      if (token) {
        console.log("Fetching fresh permissions from server...");
        const success = await dispatch("refreshPermissions");
        if (success) {
          console.log("Fresh permissions loaded and applied successfully");
        } else {
          console.warn("Using cached permissions (server refresh failed)");
        }
      }
    } catch (error) {
      console.error("Error loading auth data:", error);
      // Try to load what we can from localStorage
      try {
        const roles = localStorage.getItem("userRoles");
        const permissions = localStorage.getItem("userPermissions");
        const allowedMenuItems = localStorage.getItem("allowedMenuItems");

        if (roles) commit("SET_ROLES", JSON.parse(roles));
        if (permissions) commit("SET_PERMISSIONS", JSON.parse(permissions));
        if (allowedMenuItems) commit("SET_ALLOWED_MENU_ITEMS", JSON.parse(allowedMenuItems));
      } catch (fallbackError) {
        console.error("Error loading from localStorage fallback:", fallbackError);
      }
    }
  },

  // Refresh permissions from server (for real-time updates)
  async refreshPermissions({ commit, dispatch, state }) {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        console.warn("Cannot refresh permissions: missing token");
        return false;
      }

      // Construct baseurl same way as main.js
      let APP_HOST = window.location.origin.toString();
      if (process.env.VUE_APP_NODE_URL && process.env.VUE_APP_NODE_URL.startsWith("http")) {
        APP_HOST = process.env.VUE_APP_NODE_URL;
      } else if (process.env.VUE_APP_NODE_URL) {
        APP_HOST = APP_HOST + process.env.VUE_APP_NODE_URL;
      }

      const endpoint = `${APP_HOST}permission/list`;
      console.log("Calling API:", endpoint);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 403) {
        // User has been deactivated - logout
        console.warn("User account deactivated (403), logging out");
        await dispatch("clearAuthData");
        if (window.location.pathname !== '/authentication/page-login') {
          window.location.href = '/authentication/page-login';
        }
        return false;
      }

      if (!response.ok) {
        console.error("Failed to refresh permissions. HTTP Status:", response.status);
        const errorText = await response.text().catch(() => 'Unable to read error');
        console.error("Response:", errorText);
        return false;
      }

      const data = await response.json();
      console.log("API Response:", data);

      if (data.code === '0014-100') {
        const permissionData = data.details;

        console.log("🎉 Received fresh permissions from server:", {
          roles: permissionData.roles || [],
          permissions: permissionData.permissions || [],
          allowedMenuItems: permissionData.allowedMenuItems || []
        });

        // Effective HQ-filter BEFORE applying this refresh, to detect a real change.
        const prevHqFilterActive = isHqFilterActive(state.roles, state.canProcessHqTx);

        // Update Vuex store
        commit('SET_ROLES', permissionData.roles || []);
        commit('SET_PERMISSIONS', permissionData.permissions || []);
        commit('SET_ALLOWED_MENU_ITEMS', permissionData.allowedMenuItems || []);
        commit('SET_CAN_PROCESS_HQ_TX', !!permissionData.canProcessHqTx);

        // Decide whether the transaction list should react. The first authoritative
        // load of a session is adopted silently as the baseline (the app simply
        // learning the user's real HQ capability — not an access change). Only a
        // genuine later change to the effective HQ-filter notifies listeners.
        const nextHqFilterActive = isHqFilterActive(
          permissionData.roles || [],
          !!permissionData.canProcessHqTx,
        );
        if (!state.hqBaselineSet) {
          commit('SET_HQ_BASELINE_SET', true);
        } else if (nextHqFilterActive !== prevHqFilterActive) {
          window.dispatchEvent(
            new CustomEvent('hq-access-changed', { detail: { active: nextHqFilterActive } }),
          );
        }

        // Update localStorage
        localStorage.setItem('userRoles', JSON.stringify(permissionData.roles || []));
        localStorage.setItem('userPermissions', JSON.stringify(permissionData.permissions || []));
        localStorage.setItem('allowedMenuItems', JSON.stringify(permissionData.allowedMenuItems || []));
        localStorage.setItem('canProcessHqTx', JSON.stringify(!!permissionData.canProcessHqTx));

        console.log("Store and localStorage updated with fresh permissions");
        return true;
      }

      console.error("Unexpected response code:", data.code);
      return false;
    } catch (error) {
      console.error("Error refreshing permissions:", error.message || error);
      console.error("Full error:", error);
      return false;
    }
  },

  // Clear all auth data (logout)
  clearAuthData({ commit }) {
    // commit("SET_USER", null);
    commit("SET_ROLES", []);
    commit("SET_PERMISSIONS", []);
    commit("SET_ALLOWED_MENU_ITEMS", []);
    commit("SET_CAN_PROCESS_HQ_TX", false);
    commit("SET_TOKEN", null);

    // Clear localStorage
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("userRoles");
    localStorage.removeItem("userPermissions");
    localStorage.removeItem("allowedMenuItems");
    localStorage.removeItem("canProcessHqTx");
    // localStorage.removeItem("userName");
    // localStorage.removeItem("rolename");
  },
};

// mutations
const mutations = {
  // SET_USER(state, user) {
  //   state.user = user;
  // },
  SET_ROLES(state, roles) {
    state.roles = roles;
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
  },
  SET_ALLOWED_MENU_ITEMS(state, allowedMenuItems) {
    state.allowedMenuItems = allowedMenuItems;
  },
  SET_CAN_PROCESS_HQ_TX(state, canProcessHqTx) {
    state.canProcessHqTx = canProcessHqTx;
  },
  SET_HQ_BASELINE_SET(state, value) {
    state.hqBaselineSet = value;
  },
  SET_TOKEN(state, token) {
    state.token = token;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};